import style from 'css/admin/dashboard.module.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ReactComponent as LicenseIcon } from "asset/license_icon.svg";
import { ReactComponent as InuseIcon } from "asset/inuse_icon.svg";
import { ReactComponent as RemainIcon } from "asset/remain_icon.svg";
import { ReactComponent as WaitingIcon } from "asset/waiting_icon.svg";
import UsageBar from 'component/admin/dashboard/usageBar';
import GraphIndex from 'component/admin/dashboard/graphIndex';
import CountUp from 'component/common/CountUp';
import Collapse from 'component/common/Collapse';
import { DownOutlined } from '@ant-design/icons';
import axios from 'axios';


function Dashboard() {
    const [init, setInit] = useState(true);

    const [expandedSet, setExpandedSet] = useState(() => new Set());
    const [productExpandedSet, setProductExpandedSet] = useState(() => new Set());
    const [selectedProductKey, setSelectedProductKey] = useState(null);

    const [dataList, setDataList] = useState([]);

    const getLicenseList = useCallback(() => {
        axios.get("/product/detail")
            .then(res => {
                if (res.status === 200 && Array.isArray(res.data)) {
                    const result = res.data;

                    // 제조사 코드 기준으로 그룹핑
                    const manufacturerCodeToGroup = new Map();
                    for (const product of result) {
                        const manufacturerCode = product.MANUFACTURER_CD;
                        let group = manufacturerCodeToGroup.get(manufacturerCode);
                        if (group == null) {
                            group = {
                                MANUFACTURER_CD: manufacturerCode,
                                MANUFACTURER_NM: product.MANUFACTURER_NM,
                                PRODUCT_LIST: []
                            };
                            manufacturerCodeToGroup.set(manufacturerCode, group);
                        }
                        group.PRODUCT_LIST.push(product);
                    }

                    const grouped = Array.from(manufacturerCodeToGroup.values());
                    setDataList(grouped);

                    // 기본 선택: 첫 제조사의 첫 제품
                    if (grouped.length > 0 && grouped[0].PRODUCT_LIST?.length > 0) {
                        const firstGroup = grouped[0];
                        const firstProduct = firstGroup.PRODUCT_LIST[0];
                        const manuCode = firstGroup.MANUFACTURER_CD;
                        const prodKey = `${manuCode}:${firstProduct.PRODUCT_CD}`;
                        setExpandedSet(new Set([manuCode]));
                        setProductExpandedSet(new Set([prodKey]));
                        setSelectedProductKey(prodKey);
                    }
                } else {
                    setDataList([{ key: 'ALL', label: '전체' }]);
                }
            })
            .catch(err => {
                console.log(err)
                setDataList([{ key: 'ALL', label: '전체' }]);
            })
    }, [])

    const toggleManufacturer = useCallback((manufacturerCode) => {
        setExpandedSet((prev) => {
            const next = new Set(prev);
            const isOpen = next.has(manufacturerCode);

            if (isOpen) {
                // 현재 선택된 제품이 이 제조사에 속하면 닫지 않음 (항상 하나는 열려있도록)
                if (selectedProductKey && selectedProductKey.startsWith(`${manufacturerCode}:`)) {
                    return prev;
                }
                next.delete(manufacturerCode);
                return next;
            }

            // 다른 제조사를 여는 경우: 해당 제조사만 열고, 첫 제품을 자동 선택/확장
            const group = dataList.find((m) => m.MANUFACTURER_CD === manufacturerCode);
            let firstProdKey = null;
            if (group?.PRODUCT_LIST?.length > 0) {
                firstProdKey = `${manufacturerCode}:${group.PRODUCT_LIST[0].PRODUCT_CD}`;
            }
            if (firstProdKey) {
                setProductExpandedSet(new Set([firstProdKey]));
                setSelectedProductKey(firstProdKey);
            } else {
                setProductExpandedSet(new Set());
            }
            return new Set([manufacturerCode]);
        });
    }, [dataList, selectedProductKey]);

    const toggleProduct = useCallback((productKey) => {
        setProductExpandedSet((prev) => {
            const isOpen = prev.has(productKey);
            if (isOpen) {
                // 이미 열린 제품이면 닫지 않음
                return prev;
            }
            // 다른 제품을 클릭하면 해당 제품만 열림
            setSelectedProductKey(productKey);
            return new Set([productKey]);
        });
    }, []);

    // 선택된 제품 객체 찾기
    const selectedProduct = useMemo(() => {
        if (!selectedProductKey) return null;
        for (const m of dataList) {
            for (const p of (m.PRODUCT_LIST || [])) {
                const key = `${m.MANUFACTURER_CD}:${p.PRODUCT_CD}`;
                if (key === selectedProductKey) return p;
            }
        }
        return null;
    }, [dataList, selectedProductKey]);

    const formatNumber = useCallback((v) => {
        const n = Number(v);
        return Number.isNaN(n) ? v : n.toLocaleString();
    }, []);

    useEffect(() => {
        if (init) {
            getLicenseList();
            setInit(false);
        }
    }, [getLicenseList, init])

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className={style.header}>
                {[
                    { key: 'total', title: '보유 라이선스', Icon: LicenseIcon, value: selectedProduct?.LICENSE_CNT },
                    { key: 'inuse', title: '사용 중', Icon: InuseIcon, value: selectedProduct?.COMPLETE_CNT },
                    { key: 'remain', title: '미사용', Icon: RemainIcon, value: selectedProduct?.REMAIN_CNT ?? ((selectedProduct?.LICENSE_CNT ?? 0) - (selectedProduct?.COMPLETE_CNT ?? 0)) },
                    { key: 'wait', title: '신청 대기', Icon: WaitingIcon, value: selectedProduct?.WAIT_CNT },
                ].map(({ key, title, Icon, value }) => (
                    <div key={key} className={style.card}>
                        <div className={style.label}>
                            <div className={style.icon_container}>
                                <Icon className={style.icon} />
                            </div>
                            <span className={style.title}>{title}</span>
                        </div>
                        <div className={style.stat}>
                            <CountUp value={value ?? 0} durationMs={700} formatter={formatNumber} /> <span>개</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className={style.content}>
                {dataList.map((manufacturer) => (
                    <div key={manufacturer.MANUFACTURER_CD} className={style.manu_container}>
                        <div
                            className={style.manu_header}
                            onClick={() => toggleManufacturer(manufacturer.MANUFACTURER_CD)}
                        >
                            <span>{manufacturer.MANUFACTURER_NM} 라이선스</span>
                            <DownOutlined
                                className={`${style.chevron} ${expandedSet.has(manufacturer.MANUFACTURER_CD) ? style.chevron_open : ""}`}
                                style={{ fontSize: 14, color: '#121212' }}
                            />
                        </div>
                        <Collapse
                            isOpen={expandedSet.has(manufacturer.MANUFACTURER_CD)}
                            className={`${style.manu_products} ${style.collapse}`}
                        >
                            {manufacturer.PRODUCT_LIST?.map((p) => (
                                <div
                                    key={`${manufacturer.MANUFACTURER_CD}:${p.PRODUCT_CD}`}
                                    className={style.product_row}
                                >
                                    <div
                                        className={style.product_header}
                                        onClick={() => toggleProduct(`${manufacturer.MANUFACTURER_CD}:${p.PRODUCT_CD}`)}
                                    >
                                        <div className={style.product_label}>
                                            {(() => {
                                                const defaultSrc = `${process.env.REACT_APP_CLIENT_URL}icon/logo.jpg`;
                                                const prodPathNm = (p?.MANUFACTURER_NM ?? manufacturer?.MANUFACTURER_NM)
                                                    ? String(p?.MANUFACTURER_NM ?? manufacturer?.MANUFACTURER_NM).toLowerCase()
                                                    : "";
                                                const imgNm = p?.IMG_NM ? String(p.IMG_NM) : "";
                                                const src = imgNm
                                                    ? `${process.env.REACT_APP_CLIENT_URL}img/product/${prodPathNm}/${imgNm}`
                                                    : defaultSrc;
                                                const alt = p?.PRODUCT_NM ?? "Product";
                                                return (
                                                    <img
                                                        src={src}
                                                        alt={alt}
                                                        className={style.product_icon}
                                                        onError={(e) => {
                                                            e.currentTarget.onerror = null;
                                                            e.currentTarget.src = defaultSrc;
                                                        }}
                                                    />
                                                );
                                            })()}
                                            <span className={style.name}>{p.PRODUCT_NM}</span>
                                        </div>
                                    </div>
                                    <Collapse
                                        isOpen={productExpandedSet.has(`${manufacturer.MANUFACTURER_CD}:${p.PRODUCT_CD}`)}
                                        className={`${style.product_stat} ${style.collapse}`}
                                    >
                                        <div className={style.product_stat_inner}>
                                            <UsageBar total={p.LICENSE_CNT} used={p.COMPLETE_CNT} />
                                            <GraphIndex total={p.LICENSE_CNT} used={p.COMPLETE_CNT} />
                                        </div>
                                    </Collapse>
                                </div>
                            ))}
                        </Collapse>
                    </div>
                ))}
            </div>

        </div>
    )
}

export default Dashboard
